'use client';

import {
  faBrowser,
  faCalendarDays,
  faTimes,
} from '@fortawesome/pro-light-svg-icons';
import { Professional } from '@medprev-web-app/api-graphql-client';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';
import { useEffect, useRef } from 'react';

import { resolveProfessionalName } from './professional-wrapper.component';

import { Button } from '@/components/ui/button/button.component';
import { Icon } from '@/components/ui/icon.component';
import { Link } from '@/components/ui/link/link.component';
import { Modal } from '@/components/ui/modal.component';
import { useAgendamentoNavigation } from '@/hooks/navigation/use-agendamento-navigation.hook';
import { getAnalyticsPlugin } from '@/app/(main)/_components/logger/analytics/get-analytics-plugin';
import { AnalyticsEnum } from '@/types/enums/analytics/analytics.enum';

interface ProfessionalRedirectModalProps {
  open: boolean;
  professional: Professional;
  onOpenChange: (isOpen: boolean) => void;
}

export function ProfessionalRedirectModal({
  open,
  professional,
  onOpenChange,
}: ProfessionalRedirectModalProps) {
  const { buildLink } = useAgendamentoNavigation();
  const analytics = getAnalyticsPlugin();

  const hasTrackedRef = useRef(false);

  useEffect(() => {
    if (!open) {
      hasTrackedRef.current = false;
      return;
    }

    if (hasTrackedRef.current) {
      return;
    }

    analytics.track(
      AnalyticsEnum.PROFESSIONAL_PROFILE_REDIRECT_MODAL_SHOWN,
    );

    hasTrackedRef.current = true;
  }, [open, analytics]);

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      analytics.track(
        AnalyticsEnum.PROFESSIONAL_PROFILE_REDIRECT_MODAL_CANCELED,
      );
    }

    onOpenChange(isOpen);
  };

  return (
    <Modal open={open} onOpenChange={handleOpenChange}>
      <Modal.Content
        className="px-3 py-5"
        slotHeader={(
          <div className="flex justify-between gap-[10px] pb-0 items-start w-full">
            <div className="flex items-start gap-[10px]">
              <Icon
                icon={faBrowser}
                size="xl"
                className="text-green-400 mt-1"
              />
              <span
                data-testid={DataTestIdEnum.PROFESSIONAL_REDIRECT_MODAL_TITLE}
                className="font-bold text-2xl text-gray-900 text-left"
              >
                Aviso de redirecionamento
              </span>
            </div>
            <Modal.Close
              asChild
              className="text-gray-500 cursor-pointer self-start px-2 hover:bg-gray-50"
            >
              <Icon
                data-testid={
                  DataTestIdEnum.PROFESSIONAL_REDIRECT_MODAL_CLOSE_BUTTON
                }
                icon={faTimes}
                size="2xl"
                className="hover:text-gray-900 text-[1.625rem] "
              />
            </Modal.Close>
          </div>
        )}
      >
        <div className="mt-4 px-2">
          <p
            className="text-gray-800"
            data-testid={DataTestIdEnum.PROFESSIONAL_REDIRECT_MODAL_TEXT}
          >
            Você será redirecionado para o site de agendamentos online da
            Medprev, onde você pode ver todos os procedimentos com
            {' '}
            {resolveProfessionalName(professional)}
            {' '}
            e agendar um atendimento. É
            rápido e seguro. Clique no botão abaixo para continuar:
          </p>

          <Button
            startIcon={faCalendarDays}
            className="mt-6"
            asChild
            data-testid={
              DataTestIdEnum.PROFESSIONAL_REDIRECT_MODAL_CLOSE_LINK_REDIRECT
            }
            data-analytics-event={AnalyticsEnum.PROFESSIONAL_PROFILE_REDIRECT_MODAL_CONFIRMED}
          >
            <Link
              prefetch={false}
              href={buildLink({ path: `/professionals/${professional.id}/` })}
              target="_blank"
              rel="noopener noreferrer"
            >
              Ver consultas e horários
            </Link>
          </Button>
        </div>
      </Modal.Content>
    </Modal>
  );
}
